/**
 * Sourcing.jsx - Sourcing Adviser Page
 *
 * Evaluates and compares vendor sources for items using weighted scoring
 * across price, OTIF, lead time, quality, and risk dimensions.
 *
 * Features:
 *   - Item selector (multi-source items only)
 *   - Quantity needed input
 *   - Weighting preset dropdown
 *   - Include unapproved/expired prices checkbox
 *   - Evaluate button
 *   - Weight sliders with live total display
 *   - Save weighting button (opens modal)
 *   - Recommendation card (highlighted winner)
 *   - Source cards grid (one per vendor with scores)
 *   - Side-by-side comparison table
 *   - Record decision button (opens modal)
 *
 * Uses:
 *   - compareSourcesForItem() from '../api/sourcing'
 *   - getItems() from '../api/products'
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Award,
  Settings2,
  Save,
  CheckCircle2,
  AlertCircle,
  Package,
  Truck,
  Shield,
  Clock,
  DollarSign,
} from "lucide-react";
import { compareSourcesForItem } from "../api/sourcing";
import { getItems } from "../api/products";
import { useCompany } from "../company/CompanyContext";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import Loading from "../components/Loading";
import { useToast } from "../components/Toast";

/* ------------------------------------------------------------------ */
/* Weighting presets                                                   */
/* ------------------------------------------------------------------ */

const WEIGHT_PRESETS = {
  balanced: { price: 0.3, otif: 0.25, leadTime: 0.2, quality: 0.15, risk: 0.1 },
  cost_focused: { price: 0.5, otif: 0.2, leadTime: 0.15, quality: 0.1, risk: 0.05 },
  quality_focused: { price: 0.15, otif: 0.2, leadTime: 0.15, quality: 0.4, risk: 0.1 },
  speed_focused: { price: 0.2, otif: 0.15, leadTime: 0.4, quality: 0.15, risk: 0.1 },
  risk_averse: { price: 0.15, otif: 0.25, leadTime: 0.15, quality: 0.2, risk: 0.25 },
};

const PRESET_LABELS = {
  balanced: "Balanced (Default)",
  cost_focused: "Cost Focused",
  quality_focused: "Quality Focused",
  speed_focused: "Speed Focused",
  risk_averse: "Risk Averse",
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Sourcing() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedCompany } = useCompany();

  /* Item selection */
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [quantity, setQuantity] = useState(100);
  const [includeUnapproved, setIncludeUnapproved] = useState(false);

  /* Weighting */
  const [preset, setPreset] = useState("balanced");
  const [weights, setWeights] = useState({ ...WEIGHT_PRESETS.balanced });

  /* Evaluation results */
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  /* Modals */
  const [showSaveWeighting, setShowSaveWeighting] = useState(false);
  const [showRecordDecision, setShowRecordDecision] = useState(false);
  const [decisionForm, setDecisionForm] = useState({
    vendorNo: "",
    quantity: 0,
    reason: "",
    markPreferred: false,
  });

  /* Loading state */
  const [loadingItems, setLoadingItems] = useState(true);

  /* ------------------------------------------------------------------ */
  /* Load items on mount                                                 */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    async function loadItems() {
      try {
        setLoadingItems(true);
        const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;
        const itemsData = await getItems({ pageSize: 500, company: companyFilter });
        /* DB API returns { total, rows } */
        setItems(itemsData.rows || itemsData || []);
      } catch (err) {
        console.error("Failed to load items:", err);
        toast("Failed to load items", "bad");
      } finally {
        setLoadingItems(false);
      }
    }
    loadItems();
  }, [toast, selectedCompany]);

  /* ------------------------------------------------------------------ */
  /* Handle preset change                                                */
  /* ------------------------------------------------------------------ */

  const handlePresetChange = useCallback((presetKey) => {
    setPreset(presetKey);
    setWeights({ ...WEIGHT_PRESETS[presetKey] });
  }, []);

  /* ------------------------------------------------------------------ */
  /* Handle weight slider change                                         */
  /* ------------------------------------------------------------------ */

  const handleWeightChange = useCallback((key, value) => {
    setWeights((prev) => ({
      ...prev,
      [key]: parseFloat(value),
    }));
    setPreset("custom");
  }, []);

  /* ------------------------------------------------------------------ */
  /* Calculate total weight                                              */
  /* ------------------------------------------------------------------ */

  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const weightValid = Math.abs(totalWeight - 1) < 0.01;

  /* ------------------------------------------------------------------ */
  /* Evaluate sources                                                    */
  /* ------------------------------------------------------------------ */

  const handleEvaluate = useCallback(async () => {
    if (!selectedItem) {
      toast("Please select an item", "warn");
      return;
    }

    if (!weightValid) {
      toast("Weights must sum to 1.0 (100%)", "warn");
      return;
    }

    try {
      setEvaluating(true);
      setError(null);
      setResult(null);

      const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;
      const evaluationResult = await compareSourcesForItem(selectedItem, {
        weights,
        company: companyFilter,
      });

      setResult(evaluationResult);

      if (evaluationResult.sources.length === 0) {
        toast(evaluationResult.message || "No sources found", "warn");
      } else {
        toast("Evaluation complete", "good");
      }
    } catch (err) {
      console.error("Evaluation failed:", err);
      setError(err.message || "Evaluation failed");
      toast("Evaluation failed", "bad");
    } finally {
      setEvaluating(false);
    }
  }, [selectedItem, weights, weightValid, toast, selectedCompany]);

  /* ------------------------------------------------------------------ */
  /* Save weighting                                                      */
  /* ------------------------------------------------------------------ */

  const handleSaveWeighting = useCallback(() => {
    if (!weightValid) {
      toast("Weights must sum to 1.0 (100%)", "warn");
      return;
    }
    setShowSaveWeighting(true);
  }, [weightValid, toast]);

  const confirmSaveWeighting = useCallback(() => {
    try {
      localStorage.setItem("vms-custom-weights", JSON.stringify(weights));
      toast("Weighting saved", "good");
      setShowSaveWeighting(false);
    } catch (err) {
      console.error("Failed to save weighting:", err);
      toast("Failed to save weighting", "bad");
    }
  }, [weights, toast]);

  /* ------------------------------------------------------------------ */
  /* Record decision                                                     */
  /* ------------------------------------------------------------------ */

  const handleRecordDecision = useCallback(() => {
    if (!result || result.sources.length === 0) {
      toast("No evaluation result to record", "warn");
      return;
    }
    setDecisionForm({
      vendorNo: result.sources[0].vendorNo,
      quantity: quantity,
      reason: "",
      markPreferred: false,
    });
    setShowRecordDecision(true);
  }, [result, quantity, toast]);

  const confirmRecordDecision = useCallback(() => {
    if (!decisionForm.vendorNo) {
      toast("Please select a vendor", "warn");
      return;
    }
    if (!decisionForm.reason.trim()) {
      toast("Please provide a reason", "warn");
      return;
    }

    try {
      // Save decision to localStorage (in production, this would call an API)
      const decision = {
        itemNo: selectedItem,
        vendorNo: decisionForm.vendorNo,
        quantity: decisionForm.quantity,
        reason: decisionForm.reason,
        markPreferred: decisionForm.markPreferred,
        decidedAt: new Date().toISOString(),
      };

      const decisions = JSON.parse(localStorage.getItem("vms-sourcing-decisions") || "[]");
      decisions.push(decision);
      localStorage.setItem("vms-sourcing-decisions", JSON.stringify(decisions));

      toast("Decision recorded successfully", "good");
      setShowRecordDecision(false);
    } catch (err) {
      console.error("Failed to record decision:", err);
      toast("Failed to record decision", "bad");
    }
  }, [selectedItem, decisionForm, toast]);

  /* ------------------------------------------------------------------ */
  /* Render score bar                                                    */
  /* ------------------------------------------------------------------ */

  const renderScoreBar = (score, label) => {
    const color =
      score >= 80 ? "var(--good)" : score >= 60 ? "var(--warn)" : "var(--bad)";
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
          <span style={{ color: "var(--ink-3)", fontWeight: 600 }}>{label}</span>
          <span style={{ color: "var(--ink)", fontWeight: 650 }}>{score.toFixed(1)}</span>
        </div>
        <div style={{ height: 6, background: "var(--line-2)", borderRadius: 3, overflow: "hidden" }}>
          <div
            style={{
              width: `${score}%`,
              height: "100%",
              background: color,
              borderRadius: 3,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render source card                                                  */
  /* ------------------------------------------------------------------ */

  const renderSourceCard = (source) => {
    const isWinner = result?.recommendation?.primaryVendor?.vendorNo === source.vendorNo;
    const orderValue = source.avgUnitCost * quantity;

    return (
      <div
        key={source.vendorNo}
        className="card"
        style={{
          border: isWinner ? "2px solid var(--good)" : undefined,
          position: "relative",
        }}
      >
        {isWinner && (
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              background: "var(--good)",
              color: "#fff",
              padding: "3px 8px",
              borderRadius: 12,
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Award size={12} />
            RECOMMENDED
          </div>
        )}

        <div className="card-head">
          <div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600 }}>
              RANK #{source.rank}
            </div>
            <h3 style={{ marginTop: 2 }}>Vendor {source.vendorNo}</h3>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--accent)" }}>
              {source.compositeScore.toFixed(1)}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-3)" }}>Total Score</div>
          </div>
        </div>

        <div className="card-body">
          {/* Key metrics */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 2 }}>Landed Cost</div>
              <div style={{ fontSize: 15, fontWeight: 650 }}>
                ₱{source.avgUnitCost.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 2 }}>Order Value</div>
              <div style={{ fontSize: 15, fontWeight: 650 }}>
                ₱{orderValue.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* Score bars */}
          {renderScoreBar(source.scores.price, "Price")}
          {renderScoreBar(source.scores.otif, "OTIF")}
          {renderScoreBar(source.scores.leadTime, "Lead Time")}
          {renderScoreBar(source.scores.quality, "Quality")}
          {renderScoreBar(source.scores.risk, "Risk")}

          {/* Flags */}
          <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
            {source.transactionCount < 5 && (
              <Badge text="Low Volume" variant="warn" />
            )}
            {source.scores.otif < 60 && <Badge text="OTIF Risk" variant="bad" />}
            {source.scores.quality < 60 && <Badge text="Quality Issue" variant="bad" />}
            {source.scores.risk < 50 && <Badge text="High Risk" variant="bad" />}
            {source.compositeScore >= 80 && <Badge text="Excellent" variant="good" />}
          </div>

          {/* Open vendor button */}
          <button
            className="btn"
            style={{ width: "100%", marginTop: 12, justifyContent: "center" }}
            onClick={() => navigate(`/vendors/${source.vendorNo}`)}
          >
            Open Vendor
          </button>
        </div>
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Comparison table columns                                            */
  /* ------------------------------------------------------------------ */

  const comparisonColumns = [
    { key: "rank", label: "Rank", type: "num" },
    { key: "vendorNo", label: "Vendor" },
    { key: "compositeScore", label: "Total Score", type: "score" },
    { key: "avgUnitCost", label: "Unit Cost", type: "money" },
    { key: "scores.price", label: "Price", type: "score" },
    { key: "scores.otif", label: "OTIF", type: "score" },
    { key: "scores.leadTime", label: "Lead Time", type: "score" },
    { key: "scores.quality", label: "Quality", type: "score" },
    { key: "scores.risk", label: "Risk", type: "score" },
    { key: "transactionCount", label: "Transactions", type: "int" },
  ];

  const comparisonRows = result?.sources?.map((s) => ({
    ...s,
    "scores.price": s.scores.price,
    "scores.otif": s.scores.otif,
    "scores.leadTime": s.scores.leadTime,
    "scores.quality": s.scores.quality,
    "scores.risk": s.scores.risk,
  })) || [];

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  return (
    <div>
      <Loading show={loadingItems} text="Loading items..." />

      {/* Controls */}
      <div className="card">
        <div className="card-head">
          <h2>Sourcing Adviser</h2>
          <button className="btn primary" onClick={handleEvaluate} disabled={evaluating || !selectedItem}>
            {evaluating ? "Evaluating..." : "Evaluate"}
          </button>
        </div>
        <div className="card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            {/* Item selector */}
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Item
              </label>
              <select
                value={selectedItem}
                onChange={(e) => setSelectedItem(e.target.value)}
                style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              >
                <option value="">Select item...</option>
                {items.map((item) => (
                  <option key={item.number} value={item.number}>
                    {item.number} - {item.displayName || item.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Quantity Needed
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                min={1}
                style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              />
            </div>

            {/* Weighting preset */}
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Weighting Preset
              </label>
              <select
                value={preset}
                onChange={(e) => handlePresetChange(e.target.value)}
                style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              >
                {Object.entries(PRESET_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
                {preset === "custom" && <option value="custom">Custom</option>}
              </select>
            </div>

            {/* Include unapproved */}
            <div style={{ display: "flex", alignItems: "end" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={includeUnapproved}
                  onChange={(e) => setIncludeUnapproved(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ fontSize: 13 }}>Include unapproved/expired prices</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Weight sliders */}
      <div className="card">
        <div className="card-head">
          <h2>Scoring Weights</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: weightValid ? "var(--good)" : "var(--bad)" }}>
              Total: {(totalWeight * 100).toFixed(1)}%
            </div>
            <button className="btn" onClick={handleSaveWeighting}>
              <Save size={14} />
              Save
            </button>
          </div>
        </div>
        <div className="card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            {Object.entries(weights).map(([key, value]) => (
              <div key={key}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4, textTransform: "capitalize" }}>
                  {key === "leadTime" ? "Lead Time" : key} ({(value * 100).toFixed(1)}%)
                </label>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={value}
                  onChange={(e) => handleWeightChange(key, e.target.value)}
                  style={{ width: "100%" }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="card" style={{ borderLeft: "3px solid var(--bad)" }}>
          <div className="card-body">
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--bad)" }}>
              <AlertCircle size={18} />
              <span style={{ fontWeight: 600 }}>{error}</span>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation card */}
      {result?.recommendation && result.sources.length > 0 && (
        <div className="card" style={{ borderLeft: "3px solid var(--good)" }}>
          <div className="card-head">
            <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TrendingUp size={18} style={{ color: "var(--good)" }} />
              Recommendation
            </h2>
            <button className="btn primary" onClick={handleRecordDecision}>
              <CheckCircle2 size={14} />
              Record Decision
            </button>
          </div>
          <div className="card-body">
            <p style={{ fontSize: 14, lineHeight: 1.6 }}>{result.recommendation.message}</p>
          </div>
        </div>
      )}

      {/* Source cards grid */}
      {result?.sources?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ marginBottom: 16 }}>Source Comparison</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {result.sources.map(renderSourceCard)}
          </div>
        </div>
      )}

      {/* Side-by-side comparison table */}
      {result?.sources?.length > 1 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-head">
            <h2>Side-by-Side Comparison</h2>
          </div>
          <div className="card-body flush">
            <DataTable columns={comparisonColumns} rows={comparisonRows} />
          </div>
        </div>
      )}

      {/* Save Weighting Modal */}
      {showSaveWeighting && (
        <Modal
          title="Save Weighting Preset"
          onClose={() => setShowSaveWeighting(false)}
          footer={
            <>
              <button className="btn" onClick={() => setShowSaveWeighting(false)}>
                Cancel
              </button>
              <button className="btn primary" onClick={confirmSaveWeighting}>
                Save
              </button>
            </>
          }
        >
          <p style={{ marginBottom: 12 }}>Save the current weighting configuration as a custom preset?</p>
          <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
            <div>Price: {(weights.price * 100).toFixed(1)}%</div>
            <div>OTIF: {(weights.otif * 100).toFixed(1)}%</div>
            <div>Lead Time: {(weights.leadTime * 100).toFixed(1)}%</div>
            <div>Quality: {(weights.quality * 100).toFixed(1)}%</div>
            <div>Risk: {(weights.risk * 100).toFixed(1)}%</div>
          </div>
        </Modal>
      )}

      {/* Record Decision Modal */}
      {showRecordDecision && (
        <Modal
          title="Record Sourcing Decision"
          onClose={() => setShowRecordDecision(false)}
          footer={
            <>
              <button className="btn" onClick={() => setShowRecordDecision(false)}>
                Cancel
              </button>
              <button className="btn primary" onClick={confirmRecordDecision}>
                Record Decision
              </button>
            </>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Selected Vendor
              </label>
              <select
                value={decisionForm.vendorNo}
                onChange={(e) => setDecisionForm({ ...decisionForm, vendorNo: e.target.value })}
                style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              >
                <option value="">Select vendor...</option>
                {result?.sources?.map((s) => (
                  <option key={s.vendorNo} value={s.vendorNo}>
                    Vendor {s.vendorNo} (Score: {s.compositeScore.toFixed(1)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Order Quantity
              </label>
              <input
                type="number"
                value={decisionForm.quantity}
                onChange={(e) => setDecisionForm({ ...decisionForm, quantity: Number(e.target.value) })}
                min={1}
                style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Reason
              </label>
              <textarea
                value={decisionForm.reason}
                onChange={(e) => setDecisionForm({ ...decisionForm, reason: e.target.value })}
                rows={3}
                placeholder="Explain why this vendor was selected..."
                style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13, resize: "vertical" }}
              />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={decisionForm.markPreferred}
                onChange={(e) => setDecisionForm({ ...decisionForm, markPreferred: e.target.checked })}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 13 }}>Mark as preferred vendor for this item</span>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}
